import React, { useEffect, useMemo, useState } from 'react';
import { Search, Save, DollarSign, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { labTestsAPI, categoriesAPI } from '../../services/api';

const ITEMS_PER_PAGE = 15;

const TestPricing = () => {
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [draftPrices, setDraftPrices] = useState({});
  const [savingIds, setSavingIds] = useState({});

  const fetchTests = async () => {
    setLoading(true);
    try {
      const response = await labTestsAPI.getAll({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        search: searchQuery || undefined,
        category_id: filterCategory || undefined,
      });
      const data = response.data?.data;
      const items = data?.items || [];
      setTests(items);
      setTotalPages(data?.totalPages || 1);
      setTotalItems(data?.total || 0);
      setDraftPrices(
        items.reduce((acc, test) => {
          acc[test._id] = test.price ?? 0;
          return acc;
        }, {})
      );
    } catch (err) {
      toast.error('حدث خطأ أثناء تحميل التحاليل');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    categoriesAPI.getAll().then(res => {
      const cats = res.data?.data || res.data || [];
      setCategories(Array.isArray(cats) ? cats : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTests();
  }, [currentPage, searchQuery, filterCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCategory]);

  const handlePriceChange = (testId, value) => {
    setDraftPrices(prev => ({ ...prev, [testId]: value }));
  };

  const handleSave = async (test) => {
    const rawValue = draftPrices[test._id];
    const priceValue = rawValue === '' || rawValue === null || rawValue === undefined
      ? 0
      : Number(rawValue);
    if (Number.isNaN(priceValue) || priceValue < 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }
    setSavingIds(prev => ({ ...prev, [test._id]: true }));
    try {
      await labTestsAPI.update(test._id, { price: priceValue });
      setTests(prev =>
        prev.map(item =>
          item._id === test._id ? { ...item, price: priceValue } : item
        )
      );
      toast.success('تم حفظ السعر بنجاح');
    } catch (err) {
      toast.error('حدث خطأ أثناء حفظ السعر');
    } finally {
      setSavingIds(prev => ({ ...prev, [test._id]: false }));
    }
  };

  const canSave = (test) => {
    const rawValue = draftPrices[test._id];
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return Number(test.price || 0) !== 0;
    }
    const priceValue = Number(rawValue);
    if (Number.isNaN(priceValue)) return false;
    return priceValue !== Number(test.price || 0);
  };

  const rangeLabel = useMemo(() => {
    const start = totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const end = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
    return `${start} - ${end} من ${totalItems}`;
  }, [currentPage, totalItems]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">تحديد أسعار التحاليل</h1>
              <p className="text-slate-500 text-sm mt-1">
                إجمالي <span className="font-semibold text-slate-700">{totalItems}</span> تحليل
              </p>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ادخل اسم التحليل..."
                className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
              />
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            {categories.length > 0 && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full md:w-56 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white text-sm"
              >
                <option value="">جميع الفئات</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name_ar || cat.name_en}</option>
                ))}
              </select>
            )}
            <button
              onClick={fetchTests}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="mt-3 text-xs text-slate-500">{rangeLabel}</div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-3" />
              <p className="text-sm text-slate-500">جاري تحميل التحاليل...</p>
            </div>
          ) : tests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <DollarSign className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">لا توجد تحاليل</h3>
              <p className="text-sm text-slate-500">جرّب تعديل البحث</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم التحليل</th>
                    <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">الفئة</th>
                    <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">نوع العينة</th>
                    <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">السعر الحالي</th>
                    <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">السعر الجديد</th>
                    <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">حفظ</th>
                  </tr>
                </thead>
                <tbody>
                  {tests.map((test) => {
                    const displayName = test.name_ar || test.name_en || '-';
                    const isSaving = savingIds[test._id];
                    const hasChanges = canSave(test);
                    return (
                      <tr
                        key={test._id}
                        className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-5">
                          <div className="font-semibold text-slate-900 text-sm">{displayName}</div>
                          {test.name_en && test.name_ar && (
                            <div className="text-xs text-slate-400 mt-1" dir="ltr">{test.name_en}</div>
                          )}
                        </td>
                        <td className="px-6 py-5 hidden md:table-cell">
                          {test.category_id?.name_ar || test.category_id?.name_en ? (
                            <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-lg">
                              {test.category_id?.name_ar || test.category_id?.name_en}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-5 hidden lg:table-cell">
                          <span className="text-sm text-slate-500">{test.specimen_type || '-'}</span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="text-sm font-semibold text-slate-900 tabular-nums">
                            {Number(test.price || 0).toFixed(2)} ر.س
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={draftPrices[test._id] ?? ''}
                              onChange={(e) => handlePriceChange(test._id, e.target.value)}
                              className="w-28 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600/10 focus:border-blue-300 transition-all tabular-nums"
                            />
                            <span className="text-xs text-slate-500">ر.س</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <button
                            onClick={() => handleSave(test)}
                            disabled={isSaving || !hasChanges}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              isSaving
                                ? 'bg-slate-200 text-slate-500 cursor-wait'
                                : hasChanges
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                          >
                            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            حفظ
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600">
              صفحة {currentPage} من {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default TestPricing;
